import { useState, useMemo } from 'react';
import { HelpCircle, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

// Helper to parse question text with proper formatting
function parseQuestionText(text: string): { mainQuestion: string; subQuestions: string[] } {
  const lines = text.split('\n').filter(line => line.trim());

  // Check if the text has numbered questions (1. 2. 3. etc)
  const numberedPattern = /^\d+\.\s+/;
  const hasNumberedQuestions = lines.some(line => numberedPattern.test(line));

  if (hasNumberedQuestions) {
    // Find the main question (first line without number, or first line)
    const mainQuestion = lines.find(line => !numberedPattern.test(line)) || lines[0];
    const subQuestions = lines.filter(line => numberedPattern.test(line));
    return { mainQuestion, subQuestions };
  }

  // Otherwise treat first line as main, rest as sub-questions
  return {
    mainQuestion: lines[0] || text,
    subQuestions: lines.slice(1)
  };
}

interface QuestionOption {
  id: string;
  label: string;
  description?: string;
}

interface InteractiveQuestionFormProps {
  questionId: string;
  question: string;
  options?: QuestionOption[];
  allowMultiple?: boolean;
  allowCustom?: boolean;
  context?: string;
  onSubmit: (questionId: string, response: { selectedOptions: string[]; customText?: string }) => void;
  isSubmitting?: boolean;
  className?: string;
}

export function InteractiveQuestionForm({
  questionId,
  question,
  options = [],
  allowMultiple = false,
  allowCustom = true,
  context,
  onSubmit,
  isSubmitting = false,
  className
}: InteractiveQuestionFormProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');

  // Parse the question to handle multi-line/numbered questions
  const parsedQuestion = useMemo(() => parseQuestionText(question), [question]);

  const handleOptionToggle = (optionId: string) => {
    if (allowMultiple) {
      setSelectedOptions(prev =>
        prev.includes(optionId)
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const handleSubmit = () => {
    if (selectedOptions.length === 0 && !customText.trim()) return;

    onSubmit(questionId, {
      selectedOptions,
      customText: customText.trim() || undefined
    });
  };

  const canSubmit = selectedOptions.length > 0 || (allowCustom && customText.trim());

  return (
    <div className={cn(
      'rounded-lg border border-spark-200 dark:border-spark-800 bg-spark-50 dark:bg-spark-900/20 overflow-hidden',
      className
    )}>
      {/* Header */}
      <div className="flex items-start gap-3 px-4 py-3 bg-spark-100 dark:bg-spark-900/30 border-b border-spark-200 dark:border-spark-800">
        <HelpCircle className="w-5 h-5 text-spark-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {parsedQuestion.mainQuestion}
          </p>
          {/* Render sub-questions as a proper list */}
          {parsedQuestion.subQuestions.length > 0 && (
            <ul className="mt-2 space-y-1">
              {parsedQuestion.subQuestions.map((subQ, idx) => (
                <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 pl-2 border-l-2 border-spark-300 dark:border-spark-700">
                  {subQ}
                </li>
              ))}
            </ul>
          )}
          {context && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 p-2 bg-gray-50 dark:bg-dark-800 rounded">
              {context}
            </p>
          )}
        </div>
      </div>

      {/* Options */}
      {options.length > 0 && (
        <div className="p-4 space-y-2">
          {options.map((option) => (
            <label
              key={option.id}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                selectedOptions.includes(option.id)
                  ? 'border-spark-500 bg-spark-50 dark:bg-spark-900/30'
                  : 'border-gray-200 dark:border-dark-700 hover:border-gray-300 dark:hover:border-dark-600'
              )}
            >
              <input
                type={allowMultiple ? 'checkbox' : 'radio'}
                name={`question-${questionId}`}
                value={option.id}
                checked={selectedOptions.includes(option.id)}
                onChange={() => handleOptionToggle(option.id)}
                className="mt-0.5 w-4 h-4 text-spark-500 border-gray-300 dark:border-dark-600 focus:ring-spark-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {option.label}
                </p>
                {option.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {option.description}
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>
      )}

      {/* Custom input */}
      {allowCustom && (
        <div className="px-4 pb-4">
          {options.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px flex-1 bg-gray-200 dark:bg-dark-700" />
              <span className="text-xs text-gray-400">or provide your own response</span>
              <div className="h-px flex-1 bg-gray-200 dark:bg-dark-700" />
            </div>
          )}
          <textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Type your response..."
            rows={2}
            className={cn(
              'w-full px-3 py-2 text-sm rounded-lg border resize-none',
              'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700',
              'text-gray-900 dark:text-white placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-spark-500 focus:border-transparent'
            )}
          />
        </div>
      )}

      {/* Submit button */}
      <div className="px-4 pb-4 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          size="sm"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Submit Response
        </Button>
      </div>
    </div>
  );
}
